import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Heading, Input } from '@web/atoms';
import { useExperiment } from '@web/experiments';
import { ResponsiveImage } from '@web/molecules';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { onboardingSubmitted } from '../events';
import saleBadge from '../../images/onboarding/sale-badge60.svg';
import { Footer } from '../Footer';
import { Question } from '../Question';
import { StyledForm } from '../StyledForm';
import { schema } from './schema';
import {
  DidYouKnowContainer,
  TextContainer,
  SubheadlineText,
  ImageContainer,
  DidYouKnowTextContainer,
  SubheadlineTextOnly,
  TextOnlyContainer,
} from './styles';

export { schema } from './schema';

export const UserInfoForm = ({
  defaultValues,
  headline,
  emailLabel,
  emailQuestionCaption,
  emailQuestionPrompt,
  firstNameLabel,
  firstNameQuestionPrompt,
  goBack,
  index,
  onSubmit,
  petName,
}) => {
  const reporter = useReporter();
  const {
    formState: { errors, isSubmitting, isSubmitted, isSubmitSuccessful },
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { value } = useExperiment('EMAIL_CAPTURE_DISCOUNT', 'control');
  const submitForm = async data => {
    reporter.tag(onboardingSubmitted(index, 'user_info'));
    const result = await onSubmit(data);
    ApiResult.match(result, {
      error: {
        invalidEmail: () =>
          setError(
            'email',
            {
              type: 'server',
              message: 'Email is invalid',
            },
            { shouldFocus: true },
          ),
      },
    });
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
          keepErrors: true,
        }),
      ),
    );
  };
  const isUIVariant = value === 'banner_badge';
  const isTextVariant = value === 'slim_banner';

  const renderTextAndDiscountImage = () => (
    <DidYouKnowContainer>
      <ImageContainer>
        <ResponsiveImage
          src={saleBadge.src}
          alt="60% off"
          width={saleBadge.width}
          height={saleBadge.height}
        />
      </ImageContainer>
      <TextContainer>
        <SubheadlineText>
          Get 60% off your first box and a free bowl
        </SubheadlineText>
      </TextContainer>
    </DidYouKnowContainer>
  );

  const renderTextAndDiscount = () => (
    <DidYouKnowTextContainer>
      <TextOnlyContainer>
        <SubheadlineTextOnly>
          Get 60% off your first box and a free bowl
        </SubheadlineTextOnly>
      </TextOnlyContainer>
    </DidYouKnowTextContainer>
  );

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer $maxWidth={391}>
        {isUIVariant && renderTextAndDiscountImage()}
        {isTextVariant && renderTextAndDiscount()}
        <Heading
          typography="heading3"
          position="center"
          headingLevel="h2"
          headingText={headline}
        />
        <Question prompt={firstNameQuestionPrompt} isUIVariant={false}>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                error={error?.message}
                labelText={firstNameLabel}
                status={error?.message ? 'error' : null}
                visuallyHideLabel
                centeredAlign
              />
            )}
          />
        </Question>
        <Question
          caption={emailQuestionCaption}
          prompt={emailQuestionPrompt}
          isUIVariant={false}
        >
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                onChange={value => {
                  if (errors.email?.type === 'server') {
                    clearErrors('email');
                  }
                  field.onChange(value.trim());
                }}
                labelText={emailLabel}
                error={error?.message}
                status={error?.message ? 'error' : null}
                visuallyHideLabel
                centeredAlign
              />
            )}
          />
        </Question>
      </FormFieldsContainer>

      <Footer
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        onBackButtonClick={goBack}
      />
    </StyledForm>
  );
};

UserInfoForm.displayName = 'UserInfoForm';

UserInfoForm.propTypes = {
  defaultValues: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  emailLabel: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  emailQuestionCaption: PropTypes.string.isRequired,
  emailQuestionPrompt: PropTypes.string.isRequired,
  firstNameLabel: PropTypes.string.isRequired,
  firstNameQuestionPrompt: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
